import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import { DocusealBuilder } from '@docuseal/vue';
import {
  GET_DOCUSEAL_CONFIGURE_UI_PAYLOAD_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';
import { extract_error_message, parse_response_data } from '@/utils/api_response_helpers';

export default {
  name: 'ConfigureDocusealTemplate',
  mixins: [alert_mixin],
  components: {
    DocusealBuilder,
  },
  computed: {
    builder_roles() {
      const roles = Array.isArray(this.roles) ? this.roles : [];
      return roles
        .map((role) => {
          if (typeof role === 'string') return role;
          return String(role?.name ?? role?.role ?? role?.id ?? '').trim();
        })
        .filter(Boolean);
    },
    missing_setup_reason() {
      return this.get_missing_setup_reason();
    },
  },
  data: () => ({
    template_id: '',
    template_entity_type_id: '',
    template_name: '',
    template_type: '',
    template_form_id: '',
    onboarding_form_id: '',
    loading: false,
    token: '',
    builder_key: 0,
    roles: [],
    defined_fields: [],
  }),
  mounted() {
    this.sync_context_from_route();
    this.load_configure_ui_payload();
  },
  watch: {
    '$route.query': {
      handler() {
        this.sync_context_from_route();
        this.load_configure_ui_payload();
      },
      deep: true,
    },
  },
  methods: {
    read_trimmed_query_value(query, key) {
      const value = Reflect.get(query || {}, key);

      return String(value ?? '').trim();
    },
    get_missing_setup_reason() {
      const checks = [
        [this.template_id, 'Please upload/view a Template before configuring the DocuSeal template.'],
        [this.template_form_id, 'Please create a Template Form before configuring the DocuSeal template.'],
        [this.onboarding_form_id, 'Please link an Onboarding Form before configuring the DocuSeal template.'],
        [this.template_entity_type_id, 'Please select an assigned Entity Type before configuring the DocuSeal template.'],
      ];

      const missing = checks.find(([value]) => !String(value || '').trim());
      return missing ? missing[1] : '';
    },
    sync_context_from_route() {
      const query = this.$route?.query || {};
      this.template_id = this.read_trimmed_query_value(query, 'template_id');
      this.template_entity_type_id = this.read_trimmed_query_value(query, 'template_entity_type_id');
      this.template_name = this.read_trimmed_query_value(query, 'template_name');
      this.template_type = this.read_trimmed_query_value(query, 'template_type');
      this.template_form_id = this.read_trimmed_query_value(query, 'template_form_id');
      this.onboarding_form_id = this.read_trimmed_query_value(query, 'onboarding_form_id');

    },
    build_defined_fields_from_config_fields(rows) {
      const items = Array.isArray(rows) ? rows : [];
      const seen = new Set();
      const baseX = 120;
      const baseY = 120;
      const columnWidth = 240;
      const rowHeight = 60;

      return items
        .map((row) => ({
          name: String(row?.tag_name || row?.label || '').trim(),
          type: String(row?.type || 'text').trim() || 'text',
          role: row?.role != null ? String(row.role).trim() : null,
          required: Boolean(row?.required),
          readonly: Boolean(row?.readonly),
        }))
        .filter((field) => field.name)
        .filter((field) => {
          const key = field.name.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map((field, index) => ({
          ...field,
          x: baseX + (index % 3) * columnWidth,
          y: baseY + Math.floor(index / 3) * rowHeight,
          width: 220,
          height: 32,
        }));
    },
    reset_configure_ui_state() {
      this.token = '';
      this.roles = [];
      this.defined_fields = [];
    },
    read_configure_ui_payload_value(data, key, fallback = '') {
      const value = Reflect.get(data || {}, key);
      return String(value ?? fallback ?? '').trim();
    },

    build_configure_ui_state(data) {
      const configure_ui_state = {
        template_id: this.read_configure_ui_payload_value(
          data,
          'template_id',
          this.template_id
        ),
        template_form_id: this.read_configure_ui_payload_value(
          data,
          'template_form_id',
          this.template_form_id
        ),
        template_name: this.read_configure_ui_payload_value(
          data,
          'name',
          this.template_name
        ),
        token: String(data?.token || ''),
        roles: Array.isArray(data?.roles) ? data.roles : [],
        defined_fields: this.build_defined_fields_from_config_fields(
          data?.fields || []
        ),
        builder_key: this.builder_key + 1,
      };

      return configure_ui_state;
    },
    apply_configure_ui_payload(data) {
      Object.assign(this, this.build_configure_ui_state(data));
    },
    should_skip_configure_ui_payload_load(template_entity_type_id) {
      return !template_entity_type_id || this.loading;
    },
    handle_configure_ui_payload_failure(message) {
      this.reset_configure_ui_state();
      this.show_error(message || 'Failed to load Docuseal configuration.');
    },
    handle_configure_ui_payload_error(error) {
      this.reset_configure_ui_state();
      this.show_error(extract_error_message(error, 'Failed to load Docuseal configuration.'));
    },
    handle_configure_ui_payload_response(response) {
      const parsed = parse_response_data(response);
      if (String(parsed?.status || '').toLowerCase() !== 'success') {
        const message = String(parsed?.message || 'Failed to load Docuseal configuration.').trim();
        this.handle_configure_ui_payload_failure(message);
        return;
      }

      this.apply_configure_ui_payload(parsed?.data || {});
    },
    async load_configure_ui_payload() {
      const template_entity_type_id = Number(this.template_entity_type_id || 0) || null;
      if (this.should_skip_configure_ui_payload_load(template_entity_type_id)) {
        this.reset_configure_ui_state();
        return;
      }

      this.loading = true;
      const loader = await this.show_loader('Loading Docuseal configuration...');
      try {
        const response = await axios.post(
          GET_DOCUSEAL_CONFIGURE_UI_PAYLOAD_URL,
          { template_entity_type_id },
          get_request_config()
        );
        this.handle_configure_ui_payload_response(response);
      } catch (error) {
        this.handle_configure_ui_payload_error(error);
      } finally {
        await this.close_loader(loader);
        this.loading = false;
      }
    },
    on_fields_update(fields) {
      this.defined_fields = Array.isArray(fields) ? fields : [];
    },
  },
};
